/* ──────────────────────────────────────────────────────────────
 *  useConfigStore — configuration dialog state
 *
 *  Holds material selections, weight-pocket toggle, stiffness
 *  priorities, and calculator fields persisted across computes.
 * ────────────────────────────────────────────────────────────── */

import { create } from 'zustand';
import type { MaterialType, StiffnessPriorities, ConfigState } from '@/types/config';

/** Key-by-key shallow comparison for StiffnessPriorities records. */
function prioritiesEqual(a: StiffnessPriorities, b: StiffnessPriorities): boolean {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

/** Derive dirty flag by comparing current state against the last saved snapshot. */
function computeDirty(s: ConfigState): boolean {
  if (!s.lastSavedConfig) return false;
  const snap = s.lastSavedConfig;
  return (
    s.coreMaterial !== snap.coreMaterial ||
    s.outerMaterial !== snap.outerMaterial ||
    s.interSleeveMaterial !== snap.interSleeveMaterial ||
    s.weightPockets !== snap.weightPockets ||
    s.clampLoad !== snap.clampLoad ||
    s.allowableYield !== snap.allowableYield ||
    s.coreIdCalculator !== snap.coreIdCalculator ||
    s.sealRadial !== snap.sealRadial ||
    s.minContactArea !== snap.minContactArea ||
    !prioritiesEqual(s.stiffnessPriorities, snap.stiffnessPriorities)
  );
}

type ConfigActions = {
  setCoreMaterial: (m: MaterialType) => void;
  setOuterMaterial: (m: MaterialType) => void;
  setInterSleeveMaterial: (m: MaterialType) => void;
  setWeightPockets: (v: boolean) => void;
  setStiffnessPriorities: (p: StiffnessPriorities) => void;
  setClampLoad: (v: number | null) => void;
  setAllowableYield: (v: number | null) => void;
  setCoreIdCalculator: (v: number | null) => void;
  setSealRadial: (v: number | null) => void;
  setMinContactArea: (v: number | null) => void;
  /** Snapshot the current values as the "last saved" baseline. */
  saveSnapshot: () => void;
  /** Derived: whether config has unsaved changes vs. the snapshot. */
  dirty: boolean;
  resetConfig: () => void;
};

const INITIAL_CONFIG: ConfigState = {
  coreMaterial: 'Aluminum',
  outerMaterial: 'Aluminum',
  interSleeveMaterial: 'Aluminum',
  weightPockets: true,
  stiffnessPriorities: {},
  clampLoad: null,
  allowableYield: 260,
  coreIdCalculator: null,
  sealRadial: 0.6,
  minContactArea: null,
  lastSavedConfig: null,
};

/** Helper to set a field and recompute the dirty flag in one go. */
function setAndDirty(patch: Partial<ConfigState>): (s: ConfigState & ConfigActions) => Partial<ConfigState & ConfigActions> {
  return (s) => {
    const next = { ...s, ...patch };
    return { ...patch, dirty: computeDirty(next) };
  };
}

export const useConfigStore = create<ConfigState & ConfigActions>()(
  (set, get) => ({
    ...INITIAL_CONFIG,
    dirty: false,

    setCoreMaterial: (m) => set(setAndDirty({ coreMaterial: m })),
    setOuterMaterial: (m) => set(setAndDirty({ outerMaterial: m })),
    setInterSleeveMaterial: (m) => set(setAndDirty({ interSleeveMaterial: m })),
    setWeightPockets: (v) => set(setAndDirty({ weightPockets: v })),
    setStiffnessPriorities: (p) => set(setAndDirty({ stiffnessPriorities: p })),
    setClampLoad: (v) => set(setAndDirty({ clampLoad: v })),
    setAllowableYield: (v) => set(setAndDirty({ allowableYield: v })),
    setCoreIdCalculator: (v) => set(setAndDirty({ coreIdCalculator: v })),
    setSealRadial: (v) => set(setAndDirty({ sealRadial: v })),
    setMinContactArea: (v) => set(setAndDirty({ minContactArea: v })),

    saveSnapshot: () => {
      const s = get();
      set({
        lastSavedConfig: {
          coreMaterial: s.coreMaterial,
          outerMaterial: s.outerMaterial,
          interSleeveMaterial: s.interSleeveMaterial,
          weightPockets: s.weightPockets,
          stiffnessPriorities: { ...s.stiffnessPriorities },
          clampLoad: s.clampLoad,
          allowableYield: s.allowableYield,
          coreIdCalculator: s.coreIdCalculator,
          sealRadial: s.sealRadial,
          minContactArea: s.minContactArea,
        },
        dirty: false,
      });
    },

    resetConfig: () => set({ ...INITIAL_CONFIG, dirty: false }),
  }),
);
